import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrls } from "../utils/apiUrls";
import { callAPIWithoutAuth } from "../utils/apiUtils";
import { useLanguage } from "../context/LanguageContext";

export default function Globalsearch({ t }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
const { language } = useLanguage();
  // 🔎 Handle typing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsTyping(true);
  };

  // 🔎 Clear input
  const handleClearSearch = () => {
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // 🔎 Click suggestion
  const handleSuggestionClick = (product) => {
    setShowSuggestions(false);
    setSearchQuery("");
    navigate(`/menu-details/${product.id}?type=${product.vendor_type === "Restaurant" ? "restaurant" : "foodtruck"}&id=${product.vendor_id}`);
  };

  // 🔎 Fetch products
  const searchProducts = useCallback(async (query) => {
    if (!query.trim()) return;

    try {
      setLoader(true);

      const response = await callAPIWithoutAuth(
        apiUrls.search,
        { search: query },
        "GET",
        {},
      );

      setLoader(false);

      if (response?.data?.success) {
        setSuggestions(response.data.data || []);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Search Error:", error);
      setLoader(false);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // 🔎 Debounce search
  useEffect(() => {
    const delay = setTimeout(() => {
      if (searchQuery && isTyping) {
        searchProducts(searchQuery);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [searchQuery, isTyping, searchProducts]);

  // 🔎 Close suggestion when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setSearchQuery("");
      setSuggestions([]);
      setShowSuggestions(false);
    };

    document.addEventListener("click", handleClickOutside);

    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="search-wrapper pt-40 d-none d-md-block">
      <div className="form-group position-relative">
        {/* 🔎 Search Icon */}
        <span className="input-icon">
          <svg
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {" "}
            <path
              d="M21.7099 20.29L17.9999 16.61C19.44 14.8144 20.1374 12.5353 19.9487 10.2413C19.76 7.94731 18.6996 5.81279 16.9854 4.27665C15.2713 2.74051 13.0337 1.91951 10.7328 1.98247C8.43194 2.04543 6.24263 2.98757 4.61505 4.61515C2.98747 6.24273 2.04534 8.43204 1.98237 10.7329C1.91941 13.0338 2.74041 15.2714 4.27655 16.9855C5.81269 18.6997 7.94721 19.7601 10.2412 19.9488C12.5352 20.1375 14.8143 19.4401 16.6099 18L20.2899 21.68C20.3829 21.7737 20.4935 21.8481 20.6153 21.8989C20.7372 21.9497 20.8679 21.9758 20.9999 21.9758C21.1319 21.9758 21.2626 21.9497 21.3845 21.8989C21.5063 21.8481 21.6169 21.7737 21.7099 21.68C21.8901 21.4935 21.9909 21.2443 21.9909 20.985C21.9909 20.7257 21.8901 20.4765 21.7099 20.29ZM10.9999 18C9.61544 18 8.26206 17.5895 7.11091 16.8203C5.95977 16.0511 5.06256 14.9579 4.53275 13.6788C4.00293 12.3997 3.86431 10.9922 4.13441 9.63437C4.4045 8.2765 5.07119 7.02922 6.05016 6.05026C7.02912 5.07129 8.27641 4.4046 9.63427 4.13451C10.9921 3.86441 12.3996 4.00303 13.6787 4.53285C14.9578 5.06266 16.051 5.95987 16.8202 7.11101C17.5894 8.26216 17.9999 9.61553 17.9999 11C17.9999 12.8565 17.2624 14.637 15.9497 15.9498C14.6369 17.2625 12.8564 18 10.9999 18Z"
              fill="#555"
            />{" "}
          </svg>
        </span>

        {/* 🔎 Input */}
        <input
          type="text"
          className="form-control"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t?.searchPlaceholder || "Search products..."}
        />

        {/* ❌ Clear Button */}
        {searchQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="position-absolute border-0 bg-transparent"
            style={{
              right: "45px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "18px",
              cursor: "pointer",
              color: "#999",
            }}
          >
            ×
          </button>
        )}

        {/* ⏳ Loader */}
        {loader && (
          <div
            className="position-absolute bg-white border p-2 text-center"
            style={{
              width: "100%",
              top: "100%",
              left: 0,
              zIndex: 1000,
            }}
          >
           {t.Searching}
          </div>
        )}

        {/* 📋 Suggestions */}
        {!loader && showSuggestions && suggestions.length > 0 && (
          <ul
            className="list-unstyled shadow-sm bg-white p-2"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              maxHeight: "300px",
              overflowY: "auto",
              zIndex: 1000,
            }}
          >
            {suggestions?.map((product) => (
              <li key={product.id} className="mb-2">
                <button
                  onClick={() => handleSuggestionClick(product)}
                  className="d-flex align-items-center w-100 border-0 bg-transparent p-2 search-items-new"
                >
                  <img
                    src={
                      product?.menu_image_urls?.[0] ||
                      "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                    }
                    className="me-2 seach-items-new-image"
                    alt=""
                  />

                  <div className="text-start flex-fill d-flex align-items-start justify-content-between gap-2 serch-items-content">
                    <div>
                      <h4 className="fs-6 mb-0"> {language === "en" ? product?.en_name : product?.ja_name}</h4>
                      {product?.price && (
                        <div className="s-price">¥{Math.round(Number(product.price || 0)).toLocaleString("ja-JP")} </div>
                      )}

                      {product?.vendor_en_name && (
                        <div style={{ fontSize: "12px", color: "#777" }}>
                           {language === "en" ? product?.vendor_en_name : product?.vendor_ja_name}
                        </div>
                      )}
                    </div>
                    <span
                      className={`search-cat ${product.vendor_type === "Food Truck" ? "food" : ""}`}
                    >
                      {product.vendor_type=== "Food Truck" ? t.foodTruck :t.restaurant}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
